import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  MessageSquare, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Send,
  Bell,
  BellOff
} from 'lucide-react';
import { apiClient } from '../lib/api';
import { useUser } from '../store/useAuthStore';
import { ProductRequest, RequestStatus } from '../types/productRequest';
import { formatShortDate } from '../utils/dateUtils';

type FilterTab = 'all' | RequestStatus;

export default function Requests() {
  const [requests, setRequests] = useState<ProductRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getProductRequests();
      setRequests(data);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = activeTab === 'all' 
    ? requests 
    : requests.filter(req => req.status === activeTab);

  const getStatusCounts = () => {
    return {
      all: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      in_review: requests.filter(r => r.status === 'in_review').length,
      approved: requests.filter(r => r.status === 'approved').length,
      rejected: requests.filter(r => r.status === 'rejected').length,
    };
  };

  const statusCounts = getStatusCounts();

  const getStatusBadge = (status: RequestStatus) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'New' },
      in_review: { color: 'bg-blue-100 text-blue-800', icon: AlertCircle, label: 'In Progress' },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
    };
    return badges[status];
  };

  const toggleRequestExpansion = (requestId: string) => {
    setExpandedRequestId(expandedRequestId === requestId ? null : requestId);
  };

  const handleAddComment = async (requestId: string, message: string) => {
    try {
      await apiClient.addProductRequestComment(requestId, { message });
      // Refresh requests to show new comment
      await fetchRequests();
    } catch (err: any) {
      console.error('Failed to add comment:', err);
    }
  };


  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6" data-testid="requests-loading">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
        
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
              <div className="h-6 w-3/4 bg-gray-200 rounded mb-4"></div>
              <div className="h-4 w-full bg-gray-200 rounded mb-2"></div>
              <div className="h-4 w-2/3 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-2">Failed to load requests</div>
        <div className="text-gray-600">{error}</div>
        <button 
          onClick={fetchRequests}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Product Requests</h1>
          <p className="text-gray-600">Track your product requests and their status</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Link 
            to="/products"
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Package className="w-4 h-4 mr-2" />
            Browse Products
          </Link>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 p-1">
        <div className="flex space-x-1 overflow-x-auto">
          <TabButton 
            active={activeTab === 'all'} 
            onClick={() => setActiveTab('all')}
            count={statusCounts.all}
          >
            All
          </TabButton>
          <TabButton 
            active={activeTab === 'pending'} 
            onClick={() => setActiveTab('pending')}
            count={statusCounts.pending}
            color="yellow"
          >
            New
          </TabButton>
          <TabButton 
            active={activeTab === 'in_review'} 
            onClick={() => setActiveTab('in_review')}
            count={statusCounts.in_review}
            color="blue"
          >
            In Progress
          </TabButton>
          <TabButton 
            active={activeTab === 'approved'} 
            onClick={() => setActiveTab('approved')}
            count={statusCounts.approved}
            color="green"
          >
            Accepted
          </TabButton>
          <TabButton 
            active={activeTab === 'rejected'} 
            onClick={() => setActiveTab('rejected')}
            count={statusCounts.rejected}
            color="red"
          >
            Rejected
          </TabButton>
        </div>
      </div>

      {/* Requests List */}
      {filteredRequests.length === 0 ? (
        <EmptyState activeTab={activeTab} />
      ) : (
        <div className="space-y-4">
          {filteredRequests.map(request => (
            <RequestCard
              key={request._id}
              request={request}
              isExpanded={expandedRequestId === request._id}
              onToggle={() => toggleRequestExpansion(request._id)}
              onAddComment={handleAddComment}
              getStatusBadge={getStatusBadge}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Tab Button Component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  count: number;
  color?: 'yellow' | 'blue' | 'orange' | 'green' | 'red';
  children: React.ReactNode;
}

function TabButton({ active, onClick, count, color, children }: TabButtonProps) {
  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-800',
    blue: 'bg-blue-100 text-blue-800',
    orange: 'bg-orange-100 text-orange-800',
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
  };

  return (
    <button
      role="tab"
      onClick={onClick}
      className={`px-4 py-2 rounded-md font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
        active 
          ? 'bg-gray-100 text-gray-900' 
          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
      }`}
    >
      {children}
      {count > 0 && (
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${color ? colorClasses[color] : 'bg-gray-200 text-gray-700'}`}>
          {count}
        </span>
      )}
    </button>
  );
}

// Request Card Component
interface RequestCardProps {
  request: ProductRequest;
  isExpanded: boolean;
  onToggle: () => void;
  onAddComment: (requestId: string, message: string) => Promise<void>;
  getStatusBadge: (status: RequestStatus) => {
    color: string;
    icon: any;
    label: string;
  };
}

function RequestCard({ request, isExpanded, onToggle, onAddComment, getStatusBadge }: RequestCardProps) {
  const user = useUser();
  const isAdmin = user?.role === 'admin';
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectionInput, setShowRejectionInput] = useState(false);
  const [error, setError] = useState('');
  const [isSubscribedToRequest, setIsSubscribedToRequest] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);

  // Check subscription status from request.adminSubscriptions array
  useEffect(() => {
    if (isAdmin && user?._id) {
      const isSubscribed = request.adminSubscriptions?.includes(user._id) || false;
      setIsSubscribedToRequest(isSubscribed);
    }
  }, [isAdmin, user?._id, request.adminSubscriptions]);

  const handleSubscribeToRequest = async () => {
    setIsSubscriptionLoading(true);
    try {
      await apiClient.subscribeToProductRequest(request._id);
      setIsSubscribedToRequest(true);
      // Optimistically update the request object
      if (!request.adminSubscriptions) {
        request.adminSubscriptions = [];
      }
      if (user?._id && !request.adminSubscriptions.includes(user._id)) {
        request.adminSubscriptions.push(user._id);
      }
    } catch (err: any) {
      console.error('Failed to subscribe:', err);
      alert(err.message || 'Failed to subscribe to this request');
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

  const handleUnsubscribeFromRequest = async () => {
    setIsSubscriptionLoading(true);
    try {
      await apiClient.unsubscribeFromProductRequest(request._id);
      setIsSubscribedToRequest(false);
      // Optimistically update the request object
      if (request.adminSubscriptions && user?._id) {
        request.adminSubscriptions = request.adminSubscriptions.filter(id => id !== user._id);
      }
    } catch (err: any) {
      console.error('Failed to unsubscribe:', err);
      alert(err.message || 'Failed to unsubscribe from this request');
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

  const statusBadge = getStatusBadge(request.status);
  const StatusIcon = statusBadge.icon;
  
  // Filter out admin notes for non-admin users
  const visibleComments = request.comments.filter(
    comment => isAdmin || !comment.isAdminNote
  );

  const handleSubmitComment = async () => {
    if (!commentText.trim()) return;
    
    setIsSubmittingComment(true);
    try {
      await onAddComment(request._id, commentText);
      setCommentText('');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleStatusUpdate = async (newStatus: RequestStatus) => {
    if (newStatus === 'rejected' && !rejectionReason.trim()) {
      setShowRejectionInput(true);
      return;
    }

    setIsUpdatingStatus(true);
    setError(''); // Clear any previous errors
    
    try {
      console.log('Updating status to:', newStatus, 'for request:', request._id);
      const response = await apiClient.updateProductRequestStatus(request._id, {
        status: newStatus,
        rejectionReason: newStatus === 'rejected' ? rejectionReason : undefined
      });
      console.log('Status update response:', response);
      
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error: any) {
      console.error('Failed to update status:', error);
      setError(error.message || 'Failed to update request status. Please try again.');
      setIsUpdatingStatus(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;

    return formatShortDate(dateString);
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all"
      data-testid={`request-card-${request._id}`}
    >
      {/* Card Header - Always Visible */}
      <div 
        className="p-6 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{request.requestTitle}</h3>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {formatDate(request.createdAt)}
              </span>
              {request.requestedBy && (
                <span className="flex items-center" title={request.requestedBy.email}>
                  <span className="text-gray-400 mr-1">by</span>
                  <span className="font-medium text-gray-700">{request.requestedBy.name}</span>
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium ${
                    request.requestedBy.role === 'admin'
                      ? 'bg-purple-100 text-purple-700'
                      : request.requestedBy.role === 'distributor'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {request.requestedBy.role === 'distributor' ? 'AM' : request.requestedBy.role}
                  </span>
                </span>
              )}
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                {request.requestType.replace('_', ' ')}
              </span>
              {visibleComments.length > 0 && (
                <span className="flex items-center">
                  <MessageSquare className="w-4 h-4 mr-1" />
                  {visibleComments.length} {visibleComments.length === 1 ? 'comment' : 'comments'}
                </span>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusBadge.color}`}>
              <StatusIcon className="w-4 h-4 mr-1" />
              {statusBadge.label}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </div>
        </div>

        {/* Preview Description */}
        {!isExpanded && (
          <p className="text-gray-600 line-clamp-2">{request.requestDescription}</p>
        )}
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200">
          <div className="p-6 space-y-6">
            {/* Full Description */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
              <p className="text-gray-600">{request.requestDescription}</p>
            </div>

            {/* Request Details */}
            {(request.desiredPrice || request.quantityNeeded) && (
              <div className="grid grid-cols-2 gap-4">
                {request.desiredPrice && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">Desired Price</h4>
                    <p className="text-gray-900">${request.desiredPrice.toFixed(2)}</p>
                  </div>
                )}
                {request.quantityNeeded && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-1">Quantity Needed</h4>
                    <p className="text-gray-900">{request.quantityNeeded} units</p>
                  </div>
                )}
              </div>
            )}

            {/* Linked Product (if approved) */}
            {request.linkedProductId && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-green-900 mb-2">✅ Product Available</h4>
                <p className="text-sm text-green-700 mb-3">This request has been approved and a product has been added to the catalog.</p>
                <Link 
                  to={`/products/${request.linkedProductId}`}
                  className="inline-flex items-center text-green-700 hover:text-green-900 font-medium text-sm"
                >
                  View Product →
                </Link>
              </div>
            )}

            {/* Rejection Reason */}
            {request.rejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-red-900 mb-2">Rejection Reason</h4>
                <p className="text-sm text-red-700">{request.rejectionReason}</p>
              </div>
            )}

            {/* Admin Subscription Control */}
            {isAdmin && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">Notifications</h4>
                    <p className="text-xs text-gray-600">
                      {isSubscribedToRequest 
                        ? 'You will receive notifications for updates to this request'
                        : 'Subscribe to receive notifications for this request'}
                    </p>
                  </div>
                  <button
                    onClick={isSubscribedToRequest ? handleUnsubscribeFromRequest : handleSubscribeToRequest}
                    disabled={isSubscriptionLoading}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isSubscribedToRequest
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSubscribedToRequest ? (
                      <>
                        <BellOff className="w-4 h-4 mr-2" />
                        Unsubscribe
                      </>
                    ) : (
                      <>
                        <Bell className="w-4 h-4 mr-2" />
                        Subscribe
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Admin Status Controls */}
            {isAdmin && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Update Status</h4>
                
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
                
                {showRejectionInput && request.status !== 'rejected' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Provide a reason for rejection..."
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleStatusUpdate('pending')}
                    disabled={isUpdatingStatus || request.status === 'pending'}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      request.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed'
                        : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-300'
                    }`}
                  >
                    <Clock className="w-4 h-4 inline mr-1" />
                    New
                  </button>
                  
                  <button
                    onClick={() => handleStatusUpdate('in_review')}
                    disabled={isUpdatingStatus || request.status === 'in_review'}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      request.status === 'in_review'
                        ? 'bg-blue-100 text-blue-800 cursor-not-allowed'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-300'
                    }`}
                  >
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    In Progress
                  </button>
                  
                  <button
                    onClick={() => handleStatusUpdate('approved')}
                    disabled={isUpdatingStatus || request.status === 'approved'}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      request.status === 'approved'
                        ? 'bg-green-100 text-green-800 cursor-not-allowed'
                        : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-300'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Accept
                  </button>
                  
                  <button
                    onClick={() => {
                      if (request.status === 'rejected') return;
                      if (!rejectionReason.trim()) {
                        setShowRejectionInput(true);
                      } else {
                        handleStatusUpdate('rejected');
                      }
                    }}
                    disabled={isUpdatingStatus || request.status === 'rejected'}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      request.status === 'rejected'
                        ? 'bg-red-100 text-red-800 cursor-not-allowed'
                        : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-300'
                    }`}
                  >
                    <XCircle className="w-4 h-4 inline mr-1" />
                    Reject
                  </button>
                </div>

                {isUpdatingStatus && (
                  <div className="mt-3 text-sm text-gray-600">
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      Updating status...
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Comment Thread */}
            {visibleComments.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Activity {isAdmin && request.comments.length !== visibleComments.length && (
                    <span className="text-xs text-gray-500 font-normal ml-2">
                      ({request.comments.length - visibleComments.length} internal note{request.comments.length - visibleComments.length !== 1 ? 's' : ''})
                    </span>
                  )}
                </h4>
                <div className="space-y-4">
                  {visibleComments.map(comment => (
                    <div key={comment._id} className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                        {comment.authorRole === 'admin' ? '👔' : '👤'}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">{comment.authorName}</span>
                          {comment.authorRole === 'admin' && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
                              Admin
                            </span>
                          )}
                          {comment.isAdminNote && isAdmin && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                              Internal Note
                            </span>
                          )}
                          <span className="text-sm text-gray-500">
                            {formatDate(comment.createdAt)}
                          </span>
                        </div>
                        <p className="text-gray-700">{comment.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Comment (only if not approved or rejected) */}
            {!['approved', 'rejected'].includes(request.status) && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Add Comment</h4>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmitComment();
                      }
                    }}
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={!commentText.trim() || isSubmittingComment}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send
                  </button>
                </div>
              </div>
            )}

            {/* Collapse Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={onToggle}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium flex items-center gap-2"
              >
                <ChevronUp className="w-4 h-4" />
                Collapse
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Empty State Component
interface EmptyStateProps {
  activeTab: FilterTab;
}

function EmptyState({ activeTab }: EmptyStateProps) {
  if (activeTab === 'all') {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No product requests yet</h3>
        <p className="text-gray-600 mb-4">
          Request a product from the Products page to get started
        </p>
        <Link 
          to="/products"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Browse Products
        </Link>
      </div>
    );
  }

  const statusMessages = {
    pending: 'No new requests',
    in_review: 'No requests in progress',
    approved: 'No accepted requests',
    rejected: 'No rejected requests',
  };

  return (
    <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
      <p className="text-gray-600">{statusMessages[activeTab as RequestStatus] || 'No requests found'}</p>
    </div>
  );
}

